// Slice I1 of Inventory (docs/2026-09-20-inventory-design.md,
// Schemas/inventory-schema.md): a stored, per-household, per-week record of
// food on hand. Manual entry only -- no grocery-list integration yet (I2)
// and no purveyor boxes yet (later). Deliberately modeled on GroceryPage's
// card-and-category layout, since this is the page a household will compare
// against the grocery list once I2 lands.
import { useRef, useState } from "react";
import { useMealPlanner } from "../hooks/MealPlannerContext";
import { useIsMobile } from "../hooks/useIsMobile";
import { CATEGORIES, CATEGORY_LABELS, INVENTORY_UNIT_SUGGESTIONS, COLORS, FONT_SIZE, SPACE, RADIUS } from "../lib/constants";
import { currentWeekStart, shiftWeek, formatWeekRange, formatQuantity, parseQuantity, capitalizeFirst } from "../lib/utils";

const emptyDraft = () => ({ name: "", quantity: "", unit: "", category: "other", note: "" });

export function InventoryPage() {
  const { inventoryItems, addInventoryItem, updateInventoryItem, deleteInventoryItem, weekStart, setWeekStart, planDefaults, colors } = useMealPlanner();
  const { card, ink, soft, accent, danger, line } = colors;
  const isMobile = useIsMobile();

  const [draft, setDraft] = useState(emptyDraft());
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState(null);
  const nameInputRef = useRef(null);

  const inputStyle = {
    width: "100%",
    boxSizing: "border-box",
    padding: "var(--space-sm) var(--space-md)",
    borderRadius: RADIUS.md,
    border: `1px solid ${line}`,
    background: COLORS.card,
    color: ink,
    fontSize: FONT_SIZE.base,
    fontFamily: "var(--font-sans)",
  };

  const grouped = {};
  CATEGORIES.forEach((c) => (grouped[c] = []));
  inventoryItems.forEach((item) => (grouped[item.category] || grouped.other).push(item));
  const nonEmpty = CATEGORIES.filter((c) => grouped[c].length > 0);

  async function handleAdd() {
    const name = draft.name.trim();
    if (!name) {
      setFormError("Enter what you have.");
      return;
    }
    setBusy(true);
    setFormError("");
    try {
      await addInventoryItem({ name, quantity: parseQuantity(draft.quantity), unit: draft.unit.trim(), category: draft.category, note: draft.note.trim() });
      setDraft(emptyDraft());
      // Ready for the next item, whether Add was clicked or Enter was hit.
      nameInputRef.current?.focus();
    } catch {
      setFormError("Couldn't add that item — try again.");
    } finally {
      setBusy(false);
    }
  }

  function startEdit(item) {
    setEditingId(item.id);
    setEditDraft({ name: item.name, quantity: String(item.quantity), unit: item.unit, category: item.category, note: item.note });
  }

  async function saveEdit(id) {
    const name = editDraft.name.trim();
    if (!name) return;
    setBusy(true);
    try {
      await updateInventoryItem(id, { name, quantity: parseQuantity(editDraft.quantity), unit: editDraft.unit.trim(), category: editDraft.category, note: editDraft.note.trim() });
      setEditingId(null);
      setEditDraft(null);
    } catch {
      setFormError("Couldn't save that change — try again.");
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete(id) {
    setBusy(true);
    try {
      await deleteInventoryItem(id);
      if (editingId === id) {
        setEditingId(null);
        setEditDraft(null);
      }
    } catch {
      setFormError("Couldn't remove that item — try again.");
    } finally {
      setBusy(false);
    }
  }

  const weekNavControls = (
    <div style={{ display: "flex", alignItems: "center", gap: SPACE.md, flexWrap: "wrap" }}>
      <button className="mp-btn" onClick={() => setWeekStart(shiftWeek(weekStart, -1))} style={{ background: "transparent", border: `1px solid ${line}`, color: ink, padding: "var(--space-sm) var(--space-lg)", borderRadius: RADIUS.md, fontSize: FONT_SIZE.base }} aria-label="Previous week">
        ←
      </button>
      <span style={{ fontWeight: 700, fontSize: isMobile ? FONT_SIZE.base : FONT_SIZE.md, minWidth: isMobile ? undefined : 150, textAlign: "center" }}>Week of {formatWeekRange(weekStart)}</span>
      <button className="mp-btn" onClick={() => setWeekStart(shiftWeek(weekStart, 1))} style={{ background: "transparent", border: `1px solid ${line}`, color: ink, padding: "var(--space-sm) var(--space-lg)", borderRadius: RADIUS.md, fontSize: FONT_SIZE.base }} aria-label="Next week">
        →
      </button>
      {weekStart !== currentWeekStart(planDefaults.weekStartDay) && (
        <button className="mp-btn" onClick={() => setWeekStart(currentWeekStart(planDefaults.weekStartDay))} style={{ background: "transparent", color: accent, padding: "var(--space-sm) var(--space-md)", borderRadius: RADIUS.md, fontSize: FONT_SIZE.sm }}>
          Back to this week
        </button>
      )}
    </div>
  );

  return (
    <div>
      <datalist id="inventory-unit-suggestions">
        {INVENTORY_UNIT_SUGGESTIONS.map((u) => (
          <option key={u} value={u} />
        ))}
      </datalist>

      <div style={{ marginBottom: SPACE.xl2 }}>{weekNavControls}</div>

      <p className="mp-sans" style={{ fontSize: FONT_SIZE.sm, color: soft, marginBottom: SPACE.xl2, maxWidth: 640 }}>
        What you already have on hand for this week — from the CSA box, the market, or what's left in the fridge. This doesn't affect the grocery list yet.
      </p>

      <div style={{ background: card, border: `1px solid ${line}`, borderRadius: RADIUS.xl, padding: isMobile ? "var(--space-xxl)" : "var(--space-xxl) var(--space-x3)", marginBottom: SPACE.x3, maxWidth: 640 }}>
        <div className="mp-sans" style={{ fontSize: FONT_SIZE.sm, fontWeight: 700, color: accent, marginBottom: SPACE.md }}>
          Add an item
        </div>
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "2fr 1fr 1fr 1fr", gap: SPACE.md, marginBottom: SPACE.md }}>
          <input ref={nameInputRef} style={inputStyle} placeholder="What is it? (e.g. carrots)" value={draft.name} onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))} onKeyDown={(e) => e.key === "Enter" && handleAdd()} />
          <input style={inputStyle} placeholder="Amount" value={draft.quantity} onChange={(e) => setDraft((d) => ({ ...d, quantity: e.target.value }))} onKeyDown={(e) => e.key === "Enter" && handleAdd()} />
          <input style={inputStyle} placeholder="Unit" list="inventory-unit-suggestions" value={draft.unit} onChange={(e) => setDraft((d) => ({ ...d, unit: e.target.value }))} onKeyDown={(e) => e.key === "Enter" && handleAdd()} />
          <select style={inputStyle} value={draft.category} onChange={(e) => setDraft((d) => ({ ...d, category: e.target.value }))}>
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>{CATEGORY_LABELS[c]}</option>
            ))}
          </select>
        </div>
        <div style={{ display: "flex", gap: SPACE.md, alignItems: "center" }}>
          <input style={{ ...inputStyle, flex: 1 }} placeholder="Note (optional)" value={draft.note} onChange={(e) => setDraft((d) => ({ ...d, note: e.target.value }))} onKeyDown={(e) => e.key === "Enter" && handleAdd()} />
          <button className="mp-btn mp-sans" onClick={handleAdd} disabled={busy} style={{ background: accent, color: COLORS.onAccent, padding: "var(--space-sm) var(--space-xxl)", borderRadius: RADIUS.md, fontSize: FONT_SIZE.base, fontWeight: 600, flexShrink: 0 }}>
            Add
          </button>
        </div>
        {formError && (
          <div className="mp-sans" style={{ color: danger, fontSize: FONT_SIZE.sm, marginTop: SPACE.sm }}>
            {formError}
          </div>
        )}
      </div>

      {inventoryItems.length === 0 ? (
        <p className="mp-sans" style={{ color: soft }}>Nothing on hand for this week yet — add what's in your CSA box, market haul, or fridge above.</p>
      ) : (
        <div style={{ background: card, border: `1px solid ${line}`, borderRadius: RADIUS.xl, padding: isMobile ? "var(--space-xxl)" : "var(--space-xxl) var(--space-x3)", maxWidth: 640 }}>
          {nonEmpty.map((cat) => (
            <div key={cat} style={{ marginBottom: SPACE.xl2 }}>
              <div className="mp-sans" style={{ fontSize: FONT_SIZE.sm, fontWeight: 700, color: accent, textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: SPACE.sm }}>
                {CATEGORY_LABELS[cat]}
              </div>
              {grouped[cat].map((item) =>
                editingId === item.id ? (
                  <div key={item.id} style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "2fr 1fr 1fr 1fr auto", gap: SPACE.sm, padding: "var(--space-sm) 0", alignItems: "center" }}>
                    <input style={inputStyle} value={editDraft.name} onChange={(e) => setEditDraft((d) => ({ ...d, name: e.target.value }))} />
                    <input style={inputStyle} value={editDraft.quantity} onChange={(e) => setEditDraft((d) => ({ ...d, quantity: e.target.value }))} />
                    <input style={inputStyle} list="inventory-unit-suggestions" value={editDraft.unit} onChange={(e) => setEditDraft((d) => ({ ...d, unit: e.target.value }))} />
                    <select style={inputStyle} value={editDraft.category} onChange={(e) => setEditDraft((d) => ({ ...d, category: e.target.value }))}>
                      {CATEGORIES.map((c) => (
                        <option key={c} value={c}>{CATEGORY_LABELS[c]}</option>
                      ))}
                    </select>
                    <div style={{ display: "flex", gap: SPACE.xs, gridColumn: isMobile ? "1 / -1" : undefined }}>
                      <button className="mp-btn mp-sans" onClick={() => saveEdit(item.id)} disabled={busy} style={{ background: accent, color: COLORS.onAccent, padding: "var(--space-sm) var(--space-lg)", borderRadius: RADIUS.md, fontSize: FONT_SIZE.sm }}>
                        Save
                      </button>
                      <button className="mp-btn mp-sans" onClick={() => { setEditingId(null); setEditDraft(null); }} style={{ background: "transparent", color: soft, padding: "var(--space-sm) var(--space-lg)", borderRadius: RADIUS.md, fontSize: FONT_SIZE.sm }}>
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div key={item.id} style={{ display: "flex", alignItems: isMobile ? "flex-start" : "center", flexWrap: isMobile ? "wrap" : "nowrap", gap: isMobile ? "var(--space-hair) var(--space-lg)" : SPACE.lg, padding: "var(--space-xs) 0" }}>
                    <button
                      className="mp-btn mp-sans"
                      onClick={() => startEdit(item)}
                      style={{ background: "transparent", border: "none", color: ink, textAlign: "left", cursor: "pointer", flex: isMobile ? "1 1 auto" : 1, minWidth: 0, fontSize: isMobile ? FONT_SIZE.lg : FONT_SIZE.md, padding: 0 }}
                    >
                      {capitalizeFirst(item.name)}
                      {item.note ? ` — ${item.note}` : ""}
                    </button>
                    <span className="mp-sans" style={{ color: soft, fontSize: FONT_SIZE.base, ...(isMobile ? { flexBasis: "100%", marginLeft: 0 } : {}) }}>
                      {item.quantity > 0 ? `${formatQuantity(item.quantity)}${item.unit ? ` ${item.unit}` : ""}` : "—"}
                    </span>
                    <button
                      className="mp-btn"
                      onClick={() => handleDelete(item.id)}
                      disabled={busy}
                      title="Remove"
                      aria-label={`Remove ${capitalizeFirst(item.name)}`}
                      style={{ background: "transparent", color: danger, fontSize: isMobile ? FONT_SIZE.h3 : FONT_SIZE.lg, padding: isMobile ? "var(--space-md) var(--space-lg)" : "0 var(--space-xs)", flexShrink: 0 }}
                    >
                      ×
                    </button>
                  </div>
                )
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
